import React, { Fragment, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { api, Booking, BookingsPageVersion, IBookingsGetTabParameters, RequestStatus } from "../../logic/api";
import { Button, Dialog, DialogActions, DialogTitle } from "@mui/material";
import Scaler from "../dash/components/scaler/Scaler";
import DashPage from "../dash/DashPage";

import BookingListItem from "../../components/lists/listItems/BookingListItem/BookingListItem";
import { PagedDataView } from "../../components/lists/PagedDataView/PagedDataView";
import AnimateFrom from "../../components/animations/AnimateFrom";
import TabbedViewPage from "../../components/lists/TabbedView/TabbedViewPage";
import TabbedView from "../../components/lists/TabbedView/TabbedView";
import PackagePicker from "../../components/pickers/packagePicker/PackagePickerDialog";
import ErrorDialog from "../../components/notifications/errorDialog/ErrorDialog";
import { useLabelTemplates } from "../../components/labels/labelTemplates";
import { useLabelPrinter } from "../../components/labels";

import styles from "./styles";
import flex from "../../styles/flex";

import { SessionContext } from "../../utility/useSession";
import usePaths from "../../utility/paths";
import { useDate } from "../../utility/useDate";
import { Trans, useTranslation } from "react-i18next";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import AccessDeniedMessage from "@/components/errors/AccessDeniedMessage";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";
import { BookingsPageV2 } from "./BookingsPageV2";

class BookingSelectorController {
    onSelect = (bookingId: number) => {
        console.log(`not bound onSelect ${bookingId}`);
    }
    isSelected = (bookingId: number) => {
        console.log(`not bound isSelected ${bookingId}`);
        return false;
    }
    selectable = () => {
        return false;
    }
}

const BookingsPageV1 = () => {
    const controller = new BookingSelectorController();
    const session = useContext(SessionContext);
    const { t } = useTranslation();
    const history = useHistory();
    const { hasPermission } = useUserPermissions();
    const listBookingTabs = api.hooks.booking.listBookingTabs();
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const paths = usePaths();
    const isMeetGreetCollectionDeliveryV2Enabled = useReleaseManagement(ReleaseFlag.releaseCollectionDeliveryMeetGreetV2);
    const isMeetGreetBookingCardsEnabled = useReleaseManagement(ReleaseFlag.releaseMeetGreetBookingCards);

    useEffect(() => {
        listBookingTabs.send(session.current.context, {});
    }, []);

    const tabs: JSX.Element[] = [];

    if (listBookingTabs.data != null) {
        for (let i = 0; i < listBookingTabs.data.length; i++) {
            const tab = listBookingTabs.data[i];
            tabs.push(<TabbedViewPage key={i} title={tab.name}>
                <BookingSelector controller={controller} tabName={tab.name} requestReload={() => {
                    listBookingTabs.send(session.current.context, {});
                }}></BookingSelector>
                <PagedDataView<IBookingsGetTabParameters, Booking>
                    apiHook={api.hooks.booking.getBookingTab}
                    parameters={{
                        reportId: tab.id
                    }}
                    renderItem={(item) => {
                        return <BookingListItem
                            selected={controller.isSelected(item.id)}
                            booking={item}
                            meetGreetCollectionDeliveryEnabled={isMeetGreetCollectionDeliveryV2Enabled && isMeetGreetBookingCardsEnabled}
                            onClick={() => {
                                history.push(paths.booking(item.id));
                            }} onSelectionChanged={controller.selectable() == true ? () => {
                                controller.onSelect(item.id);
                            } : undefined} />
                    }}
                >
                    <div style={{ padding: 20, backgroundColor: "#eee" }}>
                        {t("input.validations.noBookings")}
                    </div>
                </PagedDataView>
            </TabbedViewPage>);
        }
    }

    if (!hasPermission("bookingManagement.booking.read")) {
        return (
            <DashPage>
                <AccessDeniedMessage />
            </DashPage>
        );
    }

    return <DashPage>
        <div style={styles.wrapper}>
            <div style={styles.container}>
                <div style={styles.toolBar}>
                    <Scaler>
                        <AnimateFrom delay={0.3} style={{
                            opacity: 0,
                            y: -50
                        }}><h1 style={styles.title}>{t("bookings.title")}</h1></AnimateFrom>
                    </Scaler>

                </div>

                {tabs.length == 0 && listBookingTabs.status == RequestStatus.loaded && (
                    <div style={{ padding: 20, backgroundColor: "#eee" }}>
                        {t("input.validations.noBookingTabs")}
                    </div>
                )}
                <TabbedView selectFirstIndexOnTabChanges={false} onSelectedIndexChange={(i) => { setSelectedTabIndex(i) }} selectedIndex={selectedTabIndex}>
                    {tabs}
                </TabbedView>

            </div>
        </div>
    </DashPage>
}

const BookingSelector: React.FunctionComponent<{ controller: BookingSelectorController, tabName: string, requestReload: () => void }> = (props) => {
    const [selected, setSelected] = useState<number[]>([]);
    const [showSwitchPackage, setShowSwitchPackage] = useState(false);
    const session = useContext(SessionContext);
    const switchPackage = api.hooks.packages.switchPackage();
    const fetchBooking = api.hooks.booking.getById();
    const updateBooking = api.hooks.booking.update();
    const [showProgress, setShowProgress] = useState(false);
    const [progressText, setProgressText] = useState("");
    const [showPackageChangedConfirmation, setShowPackageChangedConfirmation] = useState(false);
    const [packageConfirmationText, setPackageConfirmationText] = useState("");
    const [printConfirmationText, setPrintConfirmationText] = useState("");
    const { localizeDate } = useDate();
    const { t } = useTranslation();

    const [showPrintLabelsConfirmation, setShowPrintLabelsConfirmation] = useState(false);

    const { labelPrinter } = useLabelPrinter();
    const { generateVehiclePrintJob } = useLabelTemplates();

    props.controller.onSelect = (bookingId: number) => {
        const selectedItems = [...selected];
        const indx = selected.indexOf(bookingId);
        if (indx == -1) {
            selectedItems.push(bookingId);
        } else {
            selectedItems.splice(indx, 1);
        }
        setSelected(selectedItems);
    }
    props.controller.isSelected = (bookingId: number) => {
        return selected.indexOf(bookingId) != -1;
    }
    props.controller.selectable = () => {
        return props.tabName == t("bookings.labels.notPrinted") || props.tabName == t("bookings.labels.keysNotLoaded") || props.tabName == t("bookings.labels.exceptions");
    }
    if (selected.length == 0) {
        return <Fragment />
    }

    const switchPackages = async (targets: number[], packageCode: string) => {
        setShowSwitchPackage(false);
        setShowProgress(true);

        let count = 0;
        for (let i = 0; i < targets.length; i++) {
            setProgressText(t("booking.labels.updateBooking", { length: `${i + 1}/${targets.length}` }));
            try {
                await switchPackageForTarget(targets[i], packageCode);
                count++;
            } catch (e) {
                break;
            }
        }
        setShowProgress(false);
        setPackageConfirmationText(t("bookings.labels.packageChanged", { count: count, length: targets.length }))
        setShowPackageChangedConfirmation(true);
    }

    const printLabels = async (targets: number[]) => {
        setShowProgress(true);

        for (let i = 0; i < targets.length; i++) {
            setProgressText(t("bookings.labels.printingBooking", { length: `${i + 1}/${targets.length}` }));
            try {
                await printLabelsForTarget(targets[i]);
            } catch (e) {
                break;
            }
        }
        setShowProgress(false);
        setPrintConfirmationText(t("bookings.labels.printOk", { length: targets.length }));
        setShowPrintLabelsConfirmation(true);
    }
    const printLabelsForTarget = async (bookingId: number) => {
        return new Promise((accept, reject) => {
            fetchBooking.send(session.current.context, {
                id: bookingId
            }, (booking) => {
                if (booking == null) {
                    reject();
                    return;
                }
                labelPrinter.print(generateVehiclePrintJob(booking), () => {
                    accept(booking);

                });
            });
        });
    }
    const confirmLabelsPrinted = async (targets: number[]) => {
        setShowProgress(true);
        for (let i = 0; i < targets.length; i++) {
            setProgressText(t("bookings.labels.updatingBooking", { length: `${i + 1}/${targets.length}` }));
            try {
                await confirmLabelPrintedForTarget(targets[i]);
            } catch (e) {
                break;
            }
        }
        setShowProgress(false);
        props.requestReload();
    }
    const confirmLabelPrintedForTarget = async (bookingId: number) => {
        return new Promise((accept, reject) => {
            fetchBooking.send(session.current.context, {
                id: bookingId
            }, (booking) => {
                if (booking == null) {
                    reject();
                    return;
                }
                booking.labelsPrinted = true;
                updateBooking.send(session.current.context, booking, (booking) => {
                    if (booking == null) {
                        reject();
                        return;
                    }
                    accept(booking);
                });
            });
        });
    }
    const switchPackageForTarget = async (bookingId: number, packageCode: string) => {
        return new Promise((accept, reject) => {
            switchPackage.send(session.current.context, {
                bookingId: bookingId,
                packageCode: packageCode,
                saleDate: localizeDate(new Date()).toISO() ?? ""
            }, (booking) => {
                if (booking == null) {
                    reject();
                } else {
                    accept(booking);
                }
            });
        });
    }

    return <div>
        <div style={flex.flexRows}>
            <div style={flex.flexFill}>
                <div style={{ textAlign: "left" }}>
                    <div style={flex.flexRows}>
                        {(props.tabName == t("bookings.labels.notPrinted") || props.tabName == t("bookings.labels.keysNotLoaded") || props.tabName == t("bookings.labels.exceptions")) && (
                            <div style={{ paddingRight: 5 }}>
                                <Button onClick={async () => {
                                    setShowSwitchPackage(true);
                                }} variant="contained" color="secondary">
                                    {t("input.labels.changePackage")} ({selected.length})
                                </Button>
                            </div>
                        )}
                        {(props.tabName == t("bookings.labels.notPrinted") || props.tabName == t("bookings.labels.keysNotLoaded") || props.tabName == t("bookings.labels.exceptions")) && (
                            <div style={{ paddingRight: 5 }}>
                                <Button onClick={async () => {
                                    printLabels(selected);
                                }} variant="contained" color="secondary">{t("bookings.labels.printLabels")} ({selected.length})</Button>
                            </div>
                        )}

                    </div>
                </div>
            </div>
            <div style={flex.flexAuto}>
                <div style={{ fontSize: 16, textAlign: "right", paddingBottom: 20 }}>
                    <Trans i18nKey="bookings.labels.itemsSelected">
                        {{ length: selected.length }} item(s) selected
                    </Trans>
                </div>
            </div>
        </div>
        <Dialog open={showProgress}>
            <DialogTitle>
                {progressText}
            </DialogTitle>
        </Dialog>
        <Dialog open={showPackageChangedConfirmation}>
            <DialogTitle>
                {packageConfirmationText}
            </DialogTitle>
            <DialogActions>
                <Button onClick={() => {
                    setShowPackageChangedConfirmation(false);
                    props.requestReload();
                }}>{t("general.done")}</Button>
                <Button onClick={() => {
                    setShowPackageChangedConfirmation(false);
                    printLabels(selected);
                }}>{t("bookings.labels.printLabels")}</Button>
            </DialogActions>
        </Dialog>

        <Dialog open={showPrintLabelsConfirmation}>
            <DialogTitle>
                {printConfirmationText}
            </DialogTitle>
            <DialogActions>
                <Button onClick={() => {
                    setShowPrintLabelsConfirmation(false);
                }}>{t("general.cancel")}</Button>
                <Button onClick={() => {
                    setShowPrintLabelsConfirmation(false);
                    printLabels(selected);
                }}>{t("bookings.labels.reprint")}</Button>
                <Button onClick={() => {
                    setShowPrintLabelsConfirmation(false);
                    confirmLabelsPrinted(selected);

                }}>{t("general.yes")}</Button>
            </DialogActions>
        </Dialog>
        <ErrorDialog err={switchPackage.err}></ErrorDialog>
        <PackagePicker
            currentPackageCode={""}
            title={t("input.labels.changePackage")}
            show={showSwitchPackage}
            onClose={(newPackage) => {
                console.log(newPackage);

                if (newPackage == null) {
                    setShowSwitchPackage(false);
                    return;
                }

                switchPackages([...selected], newPackage.code);
            }}
        ></PackagePicker>

    </div>


}

const BookingsPage = () => {
    const session = useContext(SessionContext);
    const isBookingsPageV2Released = useReleaseManagement(ReleaseFlag.releaseBookingsPageV2);

    const useV2 =
        isBookingsPageV2Released &&
        session.current.site?.preferences?.bookings_page_version === BookingsPageVersion.V2;

    return useV2 ? <BookingsPageV2 /> : <BookingsPageV1 />;
};

export default BookingsPage;
